"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { MilkdownProvider } from "@milkdown/react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import {
  ComplexMarkdownEditor,
  type ComplexMarkdownEditorHandle,
  inputsToNodeOptions,
} from "./ComplexMarkdownField";
import type { EditorInput } from "./types";
import type { NodeOption } from "@/components/SpotflowV2/Sidebar/variableOptions";
import { VariableSourceIcon } from "@/components/SpotflowV2/Sidebar/VariablePickerPanel";
import { DataTypeBadge } from "@/icons/DataTypeIcon";

interface ComplexMarkdownDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string;
  onCommit: (value: string) => void;
  inputs: EditorInput[];
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  templateChips?: boolean;
  hideRawToggle?: boolean;
  disableVariables?: boolean;
}

interface VariablesSidebarProps {
  options: NodeOption[];
  onInsert: (id: string) => void;
}

export function VariablesSidebar({ options, onInsert }: VariablesSidebarProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options
      .map((node) => ({
        ...node,
        outputs: node.outputs.filter(
          (o) =>
            o.outputLabel.toLowerCase().includes(q) ||
            node.nodeName.toLowerCase().includes(q)
        ),
      }))
      .filter((node) => node.outputs.length > 0);
  }, [options, search]);

  return (
    <div className="flex w-60 shrink-0 flex-col border-r bg-muted/30">
      {/* Sidebar header */}
      <div className="shrink-0 border-b px-4 py-3">
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 h-8">
          <Search size={12} className="shrink-0 text-muted-foreground/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40 text-foreground"
          />
        </div>
      </div>

      {/* Variable list */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {options.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No variables
          </p>
        )}
        {filtered.length === 0 && options.length > 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No variables found
          </p>
        )}
        {filtered.map((node, nodeIndex) => (
          <div key={node.nodeId}>
            {nodeIndex > 0 && <div className="mx-1 my-1 h-px bg-border/50" />}

            {/* Node group header */}
            <div className="mb-1 flex items-center gap-2 px-2 pt-1">
              <VariableSourceIcon
                image={node.nodeImage}
                iconName={node.nodeIconName}
                color={node.nodeColor}
                isSecret={node.nodeId === "__secrets__"}
              />
              <span className="truncate text-sm font-normal text-muted-foreground">
                {node.nodeName}
              </span>
            </div>

            {/* Output items */}
            {node.outputs.map((output) => (
              <button
                key={output.value}
                className="mb-0.5 flex w-full items-center justify-between gap-2 rounded-md py-2 pl-10 pr-2 text-left transition-colors hover:bg-zinc-100/60"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onInsert(output.value);
                }}
              >
                <span className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">
                  {output.outputLabel}
                </span>
                <DataTypeBadge
                  dataType={output.dataType}
                  isList={output.isList}
                  className="shrink-0 leading-normal"
                />
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Renders ComplexMarkdownEditor inside a large dialog with a persistent
// variables sidebar on the left. Maintains an internal draft while open;
// commits exactly once back via `onCommit` on close so the main editor stays
// idle until the user finishes editing.
//
// Uses Dialog/DialogContent directly rather than BaseDialog because BaseDialog
// hard-caps max-h at 760px, which defeats the "make it take all available
// height" goal.
export function ComplexMarkdownDialog({
  isOpen,
  onOpenChange,
  initialValue,
  onCommit,
  inputs,
  label,
  placeholder,
  isDisabled,
  templateChips,
  hideRawToggle,
  disableVariables,
}: ComplexMarkdownDialogProps) {
  const [draft, setDraft] = useState(initialValue);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const editorRef = useRef<ComplexMarkdownEditorHandle>(null);
  const variableOptions = useMemo(() => inputsToNodeOptions(inputs), [inputs]);

  // Reset the draft every time we open the dialog so re-opening can't
  // resurrect a stale in-progress edit.
  useEffect(() => {
    if (isOpen) setDraft(initialValue);
  }, [isOpen, initialValue]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !isDisabled) {
      onCommit(draftRef.current);
    }
    onOpenChange(open);
  };

  const isSidebarVisible = useMemo(
    () => variableOptions.some((node) => node.outputs.length > 0),
    [variableOptions]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent size="6xl" className="flex flex-col min-h-[80%] p-0 gap-0">
        <DialogHeader className="shrink-0 px-6 py-4 border-b">
          <DialogTitle>{label ?? "Edit"}</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          {isSidebarVisible && (
            <VariablesSidebar
              options={variableOptions}
              onInsert={(id) => editorRef.current?.insertVariable(id)}
            />
          )}
          <div className="flex min-h-0 flex-1 flex-col p-4">
            {isOpen && (
              <MilkdownProvider>
                <ComplexMarkdownEditor
                  ref={editorRef}
                  value={draft}
                  onChange={setDraft}
                  placeholder={placeholder}
                  isDisabled={isDisabled}
                  inputs={inputs}
                  fillHeight
                  templateChips={templateChips}
                  hideRawToggle={hideRawToggle}
                  disableVariables={disableVariables}
                />
              </MilkdownProvider>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
